import h from 'hyperscript';

import { createAnchorButton } from 'ui/button/button';

import { ariaBoolean } from 'cadenza/utils/aria';
import { type EventKey, focusFirst, on, unByKey } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';

import i18n from './form-group.properties';
import styles from './form-group.module.css' assert { type: 'css' };
import './form-group.css';

const COMPONENT_NAME = 'form-group';

// We need this type, because we do a lot of duck-typing in here
// to support native form controls as well as custom ones.
type Control = HTMLElement & {
  value?: string;
  defaultValue?: string;
  checked?: boolean;
  defaultChecked?: boolean;

  disabled?: boolean;
  required?: boolean;

  min?: string;
  max?: string;

  checkValidity?: () => boolean;
  validationMessage?: string;
  validity?: ValidityState;
};

/**
 * Groups a form control and its label.
 *
 * The control is either a native control (input, select, textarea) or marked with the
 * .form-group-control CSS class.
 */
export class FormGroup extends HTMLElement {

  #observer = new MutationObserver(() => this._updateDisabled());
  shadowRoot!: ShadowRoot;

  _alert?: HTMLElement;
  _eventKeys: EventKey[] = [];

  constructor () {
    super();
    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.adoptedStyleSheets = [ styles ];
  }

  get label () {
    return this.querySelector(':scope > label') as HTMLLabelElement | null;
  }

  get control () {
    let control = null;
    [
      '.form-group-control',
      'input',
      'select',
      'textarea',
      '.form-control'
    ].some(selector => {
      // This loop is different from a single querySelector(), because it tries the selectors
      // in order. Therefore: do not alter the order!

      control = this.querySelector(selector);
      return (control != null);
    });
    return control as Control | null;
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME, 'is-upgraded');

    // Slots should be created synchronously (i.e. their creation should not be deferred until the
    // stack is clear). If not it can happen that slotted child components cannot be rendered properly.
    const controlContainer = h('slot.control-container');
    this.shadowRoot.append(controlContainer);

    // setTimeout is necessary because we don't want to execute this code
    // before the form-group is appended to the light DOM. It is necessary because
    // there are cases where input-control is a child of a child and will otherwise not be found.
    setTimeout(() => {
      const { label, control } = this;

      if (label && control) {
        label.classList.toggle('d-form-check', label.contains(control));

        if (this.resettable) {
          const resetButton = this._createResetButton(control);
          this._eventKeys.push(on(control, 'change', () => {
            resetButton.hidden = false;
          }));
          this.append(resetButton);
        }
      }

      /*
       * The form group component used to be the combination of control and its label.
       * Advanced features like links and buttons next to the label made the layout more complex
       * and we use the Shadow DOM to encapsulate this complexity.
       *
       * We do not want to mess up the layout of legacy (pre-Shadow-DOM) form groups,
       * so we use the presence of slot attributes as a switch to enable the new layout.
       */
      const usesSlots = this.querySelector(':scope > [slot]') != null;

      if (label && usesSlots) {
        this.shadowRoot.prepend(
          h('.header', [
            h('slot.label-container', { name: 'label' }),
            h('slot.label-aside', { name: 'label-aside' })
          ]));
        label.slot = 'label';
      }

      if (!usesSlots && !this.matches('.form-group-s,.form-group-l')) {
        this.classList.add('form-group-flex');
      }

      if (control) {
        controlContainer.classList.add('has-control');
        control.id ||= uniqueId();

        // connect control and label
        if (label) {
          label.htmlFor ||= control.id;

          if (label.htmlFor === control.id) {
            label.id ||= (control.id + '-label');
            control.setAttribute('aria-labelledby', label.id);
          }

          this._eventKeys.push(on(label, 'click', () => focusFirst(control)));
        }

        // setup classes
        this.classList.toggle('is-required', control.required || ariaBoolean(control, 'required'));

        /*
         * Changing the style of the control (by adding the form-control CSS class) was a bad idea.
         * For backwards-compatibility we do it only for native controls that are known to work with .form-control.
         */
        if (control.matches('input:not([type]),input[type=text],input[type=number],input[type=password],select')) {
          control.classList.add('form-control');
        }

        // observe control.disabled
        this._updateDisabled();
        this.#observer.observe(control, {
          attributes: true,
          attributeFilter: [ 'disabled', 'aria-disabled' ]
        });

        // observe control.validity
        const validationControl = control.checkValidity ? control : control.querySelector('input') as Control; // use nested <input> as fallback
        if (validationControl) {
          this._eventKeys.push(on(validationControl, 'invalid', (event) => this._onInvalid(event)));
        }

        /*
         * Add validation alert and hint elements.
         * The alert is shown if the control is invalid, the hint otherwise.
         */
        this._alert = createAlert(control.id);
        this.shadowRoot.append(this._alert);

        const validationHint = control.dataset.validationHint;
        if (validationHint) {
          const hint = createValidationHint(control.id, validationHint);
          this.shadowRoot.append(hint);
          control.setAttribute('aria-describedby', `${hint.id} ${this._alert.id}`);
        } else {
          control.setAttribute('aria-describedby', this._alert.id);
        }
      } else if (this.dataset.validationMessage) {
        const alert = createAlert();
        alert.textContent = this.dataset.validationMessage;
        this.shadowRoot.append(alert);
      }
    });
  }

  _createResetButton (control: Control) {
    const resetButton = createAnchorButton(i18n('reset'), {
      slot: 'label',
      hidden: true,
      onclick: () => {
        reset(control);
        this.dispatchEvent(new CustomEvent('reset'));
        resetButton.hidden = true;
      }
    });
    return resetButton;
  }

  // Since we have an (invalid) control, we know that the alert is defined.
  _onInvalid (event: Event) {
    event.preventDefault();
    this._alert!.textContent = this._validationMessage;
    this.classList.add('is-invalid');
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
    this._eventKeys = [];
    this.resetInvalid();
    this.#observer.disconnect();
    this.shadowRoot.innerHTML = '';
    this._alert = undefined;
  }

  _updateDisabled () {
    const control = this.control!; // In this method, we know that we have a control.
    this.classList.toggle('is-disabled', control.disabled || ariaBoolean(control, 'disabled'));
    this.resetInvalid();
  }

  resetInvalid () {
    this.classList.remove('is-invalid');

    if (this._alert) {
      this._alert.textContent = '';
    }
  }

  checkValidity () {
    return this.control?.checkValidity?.() ?? true;
  }

  get _validationMessage () {
    const control = this.control!;
    const validity = control.validity;
    const defaultMessage = control.dataset.validationMessage ?? '';
    if (validity) {
      if (validity.customError) {
        return control.validationMessage ?? ''; // custom error should win over other errors
      } else if (validity.valueMissing) {
        return control.dataset.valueMissingMessage || i18n('input.validation.required');
      } else if (validity.badInput) {
        // Defaults to default message because no specific message is defined for badInput
        return control.dataset.badInputMessage || defaultMessage;
      } else if (validity.rangeOverflow || validity.rangeUnderflow) {
        const { min, max } = control;
        if (min !== '' && max !== '') {
          return control.dataset.rangeOverflowMessage || i18n('input.validation.number', { min, max });
        } else if (min !== '') {
          return control.dataset.underflowMessage || i18n('input.validation.number.underflow', { min });
        } else {
          return control.dataset.overflowMessage || i18n('input.validation.number.overflow', { max });
        }
      } else if (validity.tooLong) {
        return control.dataset.tooLongMessage || i18n('input.validation.tooLong');
      }
    }
    return defaultMessage;
  }

  set resettable (value: boolean) {
    this.toggleAttribute('resettable', value);
  }

  get resettable () {
    return this.hasAttribute('resettable');
  }

}

customElements.define(COMPONENT_NAME, FormGroup);

function reset (control: Control) {
  if (control) {
    if (control instanceof HTMLInputElement && control.type === 'checkbox') {
      control.checked = control.defaultChecked;
    } else if (typeof control.defaultValue === 'string') {
      control.value = control.defaultValue;
    }
  }
}

export function allFormGroupsValid (parent: Element) {
  const invalidFormGroups = parent.querySelectorAll(`${COMPONENT_NAME}.is-invalid`);
  invalidFormGroups.forEach((el) => {
    const formGroup = el as FormGroup;
    if (formGroup.checkValidity()) {
      formGroup.resetInvalid();
    }
  });

  const invalidElements = parent.querySelectorAll(':invalid');
  if (invalidElements.length) {
    // trigger display of validation error message to the user
    (invalidElements[0].closest('form') as HTMLFormElement).checkValidity();
    // we have custom validation messages in our form groups (default above is fallback)
    invalidElements.forEach((el) => (el.closest(COMPONENT_NAME) as FormGroup)?.checkValidity());
    return false;
  }
  return true;
}

function createValidationHint (id: string, validationHint: string): HTMLElement {
  return h('p.validation-hint', { id: `${id}-hint`, attrs: { role: 'note' } }, validationHint);
}

function createAlert (id?: string): HTMLElement {
  return h('p.invalid-feedback', { id: id ?? `${id}-alert`, attrs: { role: 'alert' } });
}
